import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import SwitchComponent from '../switch';
import { Button } from '../button';
import { GET_CAR, UPDATE_CAR } from '../../services/carService/queries';
import { IGetCarQuery } from '../../services/carService/__generated__/queries.generated';

const Container = styled.div`
  ${tw`p-6 max-w-md`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const Helper = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

const ToggleRow = styled.div`
  ${tw`flex items-center justify-between gap-4 py-2`}
`;

const ToggleLabel = styled.span`
  ${tw`text-base font-medium text-text-primary`}
`;

const SaveButtonContainer = styled.div`
  ${tw`mt-4`}
`;

interface CarProtectionDeclinableProps {
  carId: string;
}

const CarProtectionDeclinable: React.FC<CarProtectionDeclinableProps> = ({ carId }) => {
  const [allowDecline, setAllowDecline] = useState(true);

  const { data } = useQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
    skip: !carId,
    fetchPolicy: 'cache-first',
  });

  const car = data?.car as (IGetCarQuery['car'] & { isProtectionDeclinable?: boolean }) | undefined;

  useEffect(() => {
    if (car?.isProtectionDeclinable !== undefined) {
      setAllowDecline(car.isProtectionDeclinable !== false);
    }
  }, [car?.isProtectionDeclinable]);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Protection plan settings updated');
    },
    onError: () => {
      toast.error('Error updating protection plan settings');
    },
  });

  const onSubmit = async () => {
    await updateCar({
      variables: {
        id: carId,
        updateCarInput: {
          isProtectionDeclinable: allowDecline,
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <Title>Protection plan</Title>
      </Header>

      <Section>
        <Helper>
          When off, guests cannot choose Decline coverage for this vehicle. Paid protection plans
          are still managed account-wide under Protection plans.
        </Helper>
        <ToggleRow>
          <ToggleLabel>Allow guests to decline protection</ToggleLabel>
          <SwitchComponent
            isActive={allowDecline}
            onChange={() => setAllowDecline(!allowDecline)}
            color="green"
          />
        </ToggleRow>
      </Section>

      <SaveButtonContainer>
        <Button
          theme="filled"
          text="Save"
          onClick={onSubmit}
          isDisabled={loading}
          isLoading={loading}
        />
      </SaveButtonContainer>
    </Container>
  );
};

export default CarProtectionDeclinable;
